import type { Module, Quiz } from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { addOne } from '@efdigital/ll-admin-v2/helpers/add-one.ts';
import type { ParamsProgramId } from '@efdigital/ll-admin-v2/routes/programs/program';
import type { ParamsCohortId } from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import type { ParamsModuleId } from '@efdigital/ll-admin-v2/routes/programs/program/cohort/content/module';
import { array } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { DragToSort } from '../drag-to-sort.gts';
import { EditRecord, type EditRecordSignature } from '../edit-record.gts';

export class EditModuleQuizzes extends Component<
  EditRecordSignature<
    Module,
    { params: ParamsProgramId & ParamsCohortId & ParamsModuleId }
  >
> {
  @service declare store: Store;

  setQuizValue = (property: string, quiz: Quiz, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(quiz, property, target.value);
  };

  removeQuizAt = (quizzes: Quiz[], index: number) => {
    const removed = quizzes[index];
    if (removed) {
      // Immediate rollback before removing from relationship
      removed.rollbackAttributes();
    }
    quizzes.splice(index, 1);
  };

  newQuiz = async () => {
    const quiz = this.store.createRecord<Quiz>(DataTypeQuiz.Quiz, {
      title: 'New Quiz',
      language: 'en',
      category1: 'Surveys',
      category2: 'Custom',
      category3: '',
      category4: '',
      category5: '',
      markdown: '',
      markdownOutput: '',
      markdownReadOnly: '',
      css: '',
      templateInput: 'default',
      templateOutput: 'default',
      templatePdf: 'default',
      sub: 0,
    });

    const quizzes = await this.args.item.quizzes;
    quizzes.push(quiz);
  };

  <template>
    <EditRecord
      @editLabel="Edit Quizzes"
      @item={{@item}}
      @context={{@context}}
      ...attributes
    >
      <:form as |uniqueId isDisabled isReadOnly|>
        <fieldset
          disabled={{isDisabled}}
          class="mb-0"
          id="{{uniqueId}}-quizzes"
        >
          <legend class="gud-eyebrow">Quizzes</legend>

          {{#if isReadOnly}}
            <Await @promise={{@item.quizzes}}>
              <:success as |quizzes|>
                {{#if quizzes.length}}
                  <div class="list-group mb-3">
                    {{#each quizzes as |quiz|}}
                      <LinkTo
                        @route="programs.program.cohort.content.module.quiz"
                        @models={{array
                          @params.program_id
                          @params.cohort_id
                          @params.module_id
                          quiz.id
                        }}
                        class="list-group-item"
                      >{{quiz.title}}
                        <iconify-icon inline icon="ph:arrow-right" /></LinkTo>
                    {{/each}}
                  </div>
                {{else}}
                  <p>No quizzes.</p>
                {{/if}}
              </:success>
            </Await>
          {{else}}
            <Await @promise={{@item.quizzes}}>
              <:success as |quizzes|>
                <div class="list-group mb-3 gap-1">
                  <DragToSort @items={{quizzes}}>
                    <:item as |dropZone draggable quiz index|>
                      <div class="input-group" {{dropZone index}}>
                        <span
                          class="input-group-text justify-content-center fw-bold drag-handle w-3rem"
                          {{draggable quiz index}}
                          title="Drag to reorder"
                        >
                          {{addOne index}}
                        </span>
                        <label
                          for="quiz-title-{{index}}"
                          class="visually-hidden"
                        >Quiz Title</label>
                        <input
                          type="text"
                          class="form-control list-group-item"
                          value={{quiz.title}}
                          aria-label="Quiz title"
                          required
                          {{on "change" (fn this.setQuizValue "title" quiz)}}
                        />
                        <button
                          aria-label="Remove quiz"
                          class="btn btn-outline-primary rounded-end-1"
                          type="button"
                          {{on "click" (fn this.removeQuizAt quizzes index)}}
                        ><iconify-icon inline icon="ph:trash-bold" /></button>
                      </div>
                    </:item>
                  </DragToSort>
                </div>
              </:success>
            </Await>
          {{/if}}
        </fieldset>
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <button
            type="button"
            class="btn btn-primary btn-sm"
            id="{{uniqueId}}-newQuiz"
            {{on "click" this.newQuiz}}
          >New Quiz</button>
        {{/unless}}
      </:buttons>
    </EditRecord>
  </template>
}
